import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { AccountAppDTO } from "../AccountApp";
import { AppService } from "../app.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { Subject } from "rxjs";
import { LocalStorageService } from "src/app/storage/local-storage.service";

@Component({
  selector: "app-apps-list",
  templateUrl: "./apps-list.component.html",
  styleUrls: ["./apps-list.component.scss"],
})
export class AppsListComponent implements OnInit {
  perPage: number = 20;
  pageSizeOptions = pageSizeOptions;
  searchValue: string = "";

  columns = [
    { code: "name", name: "Name", filter: true, selected: true },
    {
      code: "platform_names",
      name: "Platform(s)",
      filter: true,
      selected: true,
    },
    {
      code: "applePushConfig",
      name: "Apple Push Config",
      filter: true,
      selected: true,
    },
    {
      code: "googleCloudConfig",
      name: "Google Cloud Config",
      filter: true,
      selected: true,
    },
    {
      code: "firebaseConfig",
      name: "Firebase Config",
      filter: true,
      selected: true,
    },
    { code: "options" },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  apps!: AccountAppDTO[];
  @ViewChild("appsTable") appsTable!: MatTable<AccountAppDTO[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  appsDataSource!: MatTableDataSource<AccountAppDTO>;
  showEdit: boolean = false;
  showCreate: boolean = false;
  showDelete: boolean = false;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private Dialog: MatDialog,
    private Router: Router,
    private AppService: AppService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private PermissionService: PermissionService,
    private uiService: UiService,
    private localStorageSvc: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;
    
    this.apps = this.route.snapshot.data.apps.records.map(
      (app: AccountAppDTO) => {
        return {
          ...app,
          applePushConfig: !!app.apn_key,
          googleCloudConfig: !!app.gcm_api_key,
          firebaseConfig: !!app.firebase_config,
        };
      },
    );
    this.appsDataSource = new MatTableDataSource(this.apps);

    this.appsDataSource.filterPredicate = (data: any, filter: string) => {
      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);

      for (let selectedColumn of selectedFilterColumns) {
        if (data[selectedColumn]?.toString().toLowerCase().includes(filter)) {
          return true;
        }
      }
      return false;
    };

    Promise.all([
      this.PermissionService.categories.accountApps.hasWrite(),
      this.PermissionService.categories.accountApps.hasRemove(),
    ]).then(([hasWrite, hasRemove]) => {
      this.showEdit = hasWrite;
      this.showCreate = hasWrite;
      this.showDelete = hasRemove;
    });
  }

  ngAfterViewInit() {
    this.appsTable?.renderRows();
    this.appsDataSource.sort = this.sort;
    this.appsDataSource.paginator = this.paginator;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  reinitApps(apps: AccountAppDTO[]) {
    this.apps = apps.map((app: AccountAppDTO) => {
      return {
        ...app,
        applePushConfig: !!app.apn_key,
        googleCloudConfig: !!app.gcm_api_key,
        firebaseConfig: !!app.firebase_config,
      };
    });
    this.appsDataSource = new MatTableDataSource(this.apps);

    this.appsDataSource.filterPredicate = (data: any, filter: string) => {
      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);

      for (let selectedColumn of selectedFilterColumns) {
        if (data[selectedColumn]?.toString().toLowerCase().includes(filter)) {
          return true;
        }
      }
      return false;
    };
    this.appsTable?.renderRows();
    this.appsDataSource.sort = this.sort;
    this.appsDataSource.paginator = this.paginator;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  isCurrentApp(app: AccountAppDTO) {
    return this.SessionService.currentAppValue?.id === app.id;
  }

  applyFilter() {
    this.appsDataSource.filter = this.searchValue.trim().toLowerCase();
  }

  addNewApp() {
    this.Router.navigate(["create"], { relativeTo: this.route.parent });
  }

  toggleFilterDialog() {
    this.Dialog.open(FilterTableDialogComponent, {
      data: {
        columns: this.columns,
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.applyFilter();
        }
      });
  }

  edit(app: AccountAppDTO) {
    this.Router.navigate([app.id], { relativeTo: this.route.parent });
  }

  deleteApp(app: AccountAppDTO) {
    this.Dialog.open(AreYouSureDialogComponent, {
      data: {
        areYouSureInfo: {
          description: `Are you sure you want to delete this app ( ${app.name} )?`,
          warning: "Warning: this is not reversible!",
        },
      },
    })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        if (res) {
          this.uiService
            .waitForEvent(
              this.AppService.deleteApp(
                this.currentAccount.accountID,
                app.id,
              ).pipe(
                tap(
                  () => {
                    this.NotificationService.openSuccessSnackBar("App deleted");
                  },
                  () => {
                    this.NotificationService.openErrorSnackBar(
                      "Error deleting app",
                    );
                  },
                ),
                switchMap(() =>
                  this.AppService.getApps(this.currentAccount.accountID),
                ),
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (apps) => {
                this.reinitApps(
                  (apps as any).records as unknown as AccountAppDTO[],
                );
                this.SessionService.updateApps((apps as any).records);
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving apps",
                );
              },
            );
        }
      });
  }

  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

}
